import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink } from "react-router-dom";
import { festival, navItems } from "../data/festival";
import { useTickets } from "../context/TicketsContext";
import { IconClose, IconExternal, IconFacebook, IconInstagram } from "./icons";
import styles from "./MobileNavDrawer.module.css";

const subLinks = [
  { label: "Venue Info", path: "/about/venue" },
  { label: "FAQ", path: "/about/faq" },
  { label: "SFMA", path: "/about/sfma" },
  { label: "Press", path: "/about/press" },
  { label: "Contact", path: "/about/contact" },
  { label: "Previous Lineups", path: "/lineup/previous" },
  { label: "Vending", path: "/participate/vending" },
  { label: "Sponsors", path: "/participate/sponsors" },
  { label: "Get Involved", path: "/participate/get-involved" },
];

export default function MobileNavDrawer({ open, onClose }) {
  const { open: openTickets } = useTickets();

  // Lock body scroll while drawer is open so iOS Safari doesn't keep
  // bouncing the page underneath. Restore on close + cleanup.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  // Render to document.body via portal. Critical: the SiteHeader uses
  // `transform: translateX(-50%)` to center against the shell, and any
  // ancestor with a transform becomes the containing block for fixed
  // descendants. If the drawer were rendered inside the header, its
  // `position: fixed` would be relative to the header's tiny strip
  // (not the viewport) and the slide-in scrim would never cover the
  // page. Portaling escapes that containing-block trap.
  return createPortal(
    <>
      <div
        className={[styles.scrim, open ? styles.scrimOpen : ""].join(" ")}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={[styles.drawer, open ? styles.drawerOpen : ""].join(" ")}
        aria-label="Site menu"
        aria-hidden={!open}
        // Prevent focus from being trapped in the offscreen drawer
        // when it's closed.
        inert={!open ? "" : undefined}
      >
        <div className={styles.head}>
          <Link to="/" onClick={onClose} className={styles.brand}>
            <span className={styles.brandTop}>Rhythms</span>
            <span className={styles.brandBot}>on the Rio</span>
          </Link>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeBtn}
            aria-label="Close menu"
          >
            <IconClose />
          </button>
        </div>

        <nav className={styles.primary} aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.exact}
              onClick={onClose}
              className={({ isActive }) =>
                [styles.primaryLink, isActive ? styles.active : ""].join(" ")
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <button
          type="button"
          className={styles.tickets}
          onClick={() => {
            onClose();
            openTickets();
          }}
        >
          Buy Tickets <IconExternal />
        </button>

        <div className={styles.subTitle}>More</div>
        <ul className={styles.subList}>
          {subLinks.map((s) => (
            <li key={s.path}>
              <Link to={s.path} onClick={onClose} className={styles.subLink}>
                {s.label}
              </Link>
            </li>
          ))}
        </ul>

        <div className={styles.foot}>
          <a href={festival.facebook} target="_blank" rel="noopener" aria-label="Facebook">
            <IconFacebook />
          </a>
          <a href={festival.instagram} target="_blank" rel="noopener" aria-label="Instagram">
            <IconInstagram />
          </a>
        </div>
      </aside>
    </>,
    document.body
  );
}
